import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';

const TermsPage: React.FC = () => {
  return (
    <div className="font-display text-ink bg-white min-h-screen">
      <Helmet>
        <title>Terms of Service | DINK Social</title>
        <meta name="description" content="Read the DINK Social terms of service. Understand the rules and guidelines for using the childfree couples social platform." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Terms of Service | DINK Social" />
        <meta property="og:description" content="Read the DINK Social terms of service." />
        <meta property="og:url" content="https://dinksocial.com/terms" />
        <meta property="og:site_name" content="DINK Social" />
        <meta property="og:image" content="https://dinksocial.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service | DINK Social" />
        <meta name="twitter:description" content="Read the DINK Social terms of service." />
        <meta name="twitter:image" content="https://dinksocial.com/og-image.jpg" />
        <link rel="canonical" href="https://dinksocial.com/terms" />
      </Helmet>

      <Nav />

      {/* ─── HEADING ───────────────────────────────────────── */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16 px-6 md:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <h1
            className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px] mb-5"
            style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            Terms of service
          </h1>
          <p
            className="font-playfair italic font-medium text-ink/85 text-[20px] md:text-[24px]"
            style={{ lineHeight: 1.3, letterSpacing: '-0.015em' }}
          >
            The rules and guidelines for using DINK Social
          </p>
        </div>
      </section>

      {/* ─── BODY ──────────────────────────────────────────── */}
      <section className="pb-20 md:pb-24 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <div className="prose prose-lg max-w-none
            prose-headings:font-display prose-headings:font-medium prose-headings:text-ink prose-headings:tracking-tight
            prose-h2:text-[26px] md:prose-h2:text-[32px] prose-h2:mt-12 prose-h2:mb-5 prose-h2:leading-tight
            prose-h3:text-[20px] md:prose-h3:text-[24px] prose-h3:mt-8 prose-h3:mb-3 prose-h3:leading-tight
            prose-p:text-ink/75 prose-p:text-[16px] md:prose-p:text-[17px] prose-p:leading-relaxed
            prose-a:text-primary prose-a:no-underline prose-a:font-medium hover:prose-a:text-primary-dark hover:prose-a:underline
            prose-strong:text-ink prose-strong:font-semibold
            prose-ul:text-ink/75 prose-li:text-[16px] md:prose-li:text-[17px] prose-li:leading-relaxed
            prose-li:marker:text-primary/60">
              <p className="text-ink/75 mb-2">
                <strong>Last Updated:</strong> January 22, 2026
              </p>
              <p className="text-ink/75 mb-2">
                <strong>Effective Date:</strong> October 25, 2025
              </p>
              <p className="text-ink/75 mb-6">
                <strong>Version:</strong> 1.0
              </p>

              {/* Section 1 */}
              <h2>1. Acceptance of Terms</h2>
              <p className="text-ink/75 mb-6">
                By creating an account, accessing, or using our couple-focused social platform ("Service"), you ("User" or "you") agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
              </p>

              {/* Section 2 */}
              <h2>2. Description of Service</h2>
              <p className="text-ink/75 mb-6">
                Our Service is a social and community forum platform designed exclusively for childfree couples (DINK - Dual Income No Kids) to participate in city-based and global discussions, share experiences, and organize spontaneous meetups.
              </p>

              {/* Section 3 */}
              <h2>3. Eligibility Requirements</h2>
              <p className="text-ink/75 mb-3">To use our Service, you must:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-6 space-y-1">
                <li>Be at least 18 years old</li>
                <li>Be in a committed relationship and register as a couple</li>
                <li>Have the legal authority to enter into this agreement</li>
                <li>Comply with all applicable local, state, and national laws</li>
                <li>Provide accurate information and verify your identity when requested</li>
                <li>Respect the childfree nature of the community</li>
              </ul>

              {/* Section 4 */}
              <h2>4. Account Registration and Security</h2>

              <h3>4.1 Couple Representation</h3>
              <p className="text-ink/75 mb-4">
                When posting on the platform, you represent both partners in your couple. We encourage partners to discuss posts before publishing, though technical dual-approval is not currently required.
              </p>

              <h3>4.2 Account Responsibilities</h3>
              <p className="text-ink/75 mb-3">You are responsible for:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Providing accurate, current, and complete information</li>
                <li>Maintaining the confidentiality of your login credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
                <li>Ensuring both partners consent to all account activities</li>
              </ul>

              <h3>4.3 Partner Invitation</h3>
              <p className="text-ink/75 mb-6">
                Initial registration may be completed by one partner, but full platform features require both partners to verify their participation.
              </p>

              {/* Section 5 */}
              <h2>5. Prohibited Conduct and Content</h2>

              <h3>5.1 Prohibited Activities</h3>
              <p className="text-ink/75 mb-3">You may not:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Use the Service for illegal, harmful, or unauthorized purposes</li>
                <li>Create false or misleading profiles</li>
                <li>Impersonate others or misrepresent your relationship status</li>
                <li>Harass, stalk, threaten, or harm other users</li>
                <li>Spam, solicit, or conduct unauthorized commercial activities</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use automated systems (bots, scrapers) without permission</li>
                <li>Violate any applicable laws or regulations</li>
              </ul>

              <h3>5.2 Prohibited Content</h3>
              <p className="text-ink/75 mb-3">You may not post or share content that:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Is sexually explicit, pornographic, or adult in nature</li>
                <li>Contains hate speech, discrimination, or harassment</li>
                <li>Violates intellectual property rights</li>
                <li>Contains false or misleading information</li>
                <li>Promotes illegal activities or violence</li>
                <li>Includes personal information of others without consent</li>
              </ul>

              <h3>5.3 Forum Guidelines</h3>
              <p className="text-ink/75 mb-3">You agree to:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-6 space-y-1">
                <li>Respect the couple-based nature of all interactions</li>
                <li>Use "we/us/our" language when representing your couple</li>
                <li>Respect local based forum boundaries</li>
                <li>Not share content from local forums publicly</li>
              </ul>

              {/* Section 6 */}
              <h2>6. User Content and Intellectual Property</h2>

              <h3>6.1 Your Content</h3>
              <p className="text-ink/75 mb-4">
                You retain ownership of content you post, but grant us a license to use, copy, display, and distribute your content as necessary to provide our Service.
              </p>

              <h3>6.2 Content Standards</h3>
              <p className="text-ink/75 mb-4">
                All content must be appropriate for a general audience and align with our community guidelines for couple-focused social networking.
              </p>

              <h3>6.3 Couple Content Ownership</h3>
              <p className="text-ink/75 mb-6">
                Content created by couples is jointly owned. If one partner leaves the platform, the remaining partner may choose to maintain or remove couple-created content.
              </p>

              {/* Section 7 */}
              <h2>7. Privacy and Data Protection</h2>
              <p className="text-ink/75 mb-6">
                Your privacy is important to us. Our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>, incorporated herein by reference, explains how we collect, use, and protect your information. By using our Service, you consent to our data practices as described in the Privacy Policy.
              </p>

              {/* Section 8 */}
              <h2>8. Service Availability and Modifications</h2>

              <h3>8.1 Service Availability</h3>
              <p className="text-ink/75 mb-4">
                We strive to maintain consistent service availability but do not guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, or technical issues.
              </p>

              <h3>8.2 Service Modifications</h3>
              <p className="text-ink/75 mb-3">
                We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice. This includes the right to:
              </p>
              <ul className="list-disc pl-6 text-ink/75 mb-6 space-y-1">
                <li>Adjust city unlock thresholds</li>
                <li>Change forum categories and structure</li>
                <li>Implement new consensus requirements</li>
              </ul>

              {/* Section 9 */}
              <h2>9. Account Termination</h2>

              <h3>9.1 Termination by You</h3>
              <p className="text-ink/75 mb-4">
                You may delete your account at any time through your profile settings or by contacting us.
              </p>

              <h3>9.2 Termination by Us</h3>
              <p className="text-ink/75 mb-3">We may suspend or terminate your account if you:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-6 space-y-1">
                <li>Violate these Terms or our community guidelines</li>
                <li>Engage in prohibited conduct</li>
                <li>Fail to respond to verification requests</li>
                <li>Have not used your account for an extended period</li>
              </ul>

              {/* Section 10 */}
              <h2>10. Disclaimers and Limitation of Liability</h2>

              <h3>10.1 Service Disclaimer</h3>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-800 font-medium text-sm">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </p>
              </div>

              <h3>10.2 Limitation of Liability</h3>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-800 font-medium text-sm">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR USE.
                </p>
              </div>

              <h3>10.3 User Interactions</h3>
              <p className="text-ink/75 mb-6">
                We are not responsible for the conduct of other users or the outcomes of any meetings or relationships formed through our Service.
              </p>

              {/* Section 11 */}
              <h2>11. Indemnification</h2>
              <p className="text-ink/75 mb-6">
                You agree to indemnify and hold us harmless from any claims, damages, or expenses arising from your use of the Service, violation of these Terms, or violation of any rights of another party.
              </p>

              {/* Section 12 */}
              <h2>12. Dispute Resolution and Governing Law</h2>

              <h3>12.1 Governing Law</h3>
              <p className="text-ink/75 mb-4">
                These Terms are governed by the laws of the State of North Carolina, USA, without regard to conflict of law principles.
              </p>

              <h3>12.2 Dispute Resolution</h3>
              <p className="text-ink/75 mb-6">
                Any disputes arising from these Terms or your use of the Service shall be resolved by binding arbitration administered by JAMS in accordance with its Comprehensive Arbitration Rules and Procedures, and judgment on the award rendered by the arbitrator may be entered in any court having jurisdiction thereof. The arbitration shall take place in the State of North Carolina.
              </p>

              {/* Section 13 */}
              <h2>13. General Provisions</h2>

              <h3>13.1 Entire Agreement</h3>
              <p className="text-ink/75 mb-4">
                These Terms constitute the entire agreement between you and us regarding the Service.
              </p>

              <h3>13.2 Severability</h3>
              <p className="text-ink/75 mb-4">
                If any provision of these Terms is found unenforceable, the remaining provisions will remain in full force.
              </p>

              <h3>13.3 Assignment</h3>
              <p className="text-ink/75 mb-6">
                We may assign these Terms without notice. You may not assign your rights without our written consent.
              </p>

              {/* Section 14 */}
              <h2>14. Changes to Terms</h2>
              <p className="text-ink/75 mb-6">
                We may update these Terms periodically. We will notify users of material changes via email or through the Service. Your continued use after changes constitutes acceptance of the updated Terms.
              </p>

              {/* Section 15 */}
              <h2>15. Community Standards</h2>
              <p className="text-ink/75 mb-3">This is explicitly a childfree couples platform. Content should:</p>
              <ul className="list-disc pl-6 text-ink/75 mb-6 space-y-1">
                <li>Align with the DINK lifestyle</li>
                <li>Respect couples choosing different paths (by choice/circumstance)</li>
                <li>Not promote discrimination against parents or children</li>
                <li>Focus on couple experiences and adult-oriented activities</li>
              </ul>

              {/* Section 16 */}
              <h2>16. Contact Information</h2>
              <p className="text-ink/75 mb-4">
                For questions about these Terms of Service, please contact us at:
              </p>
              <div className="bg-ink/5 border border-ink/10 rounded-lg p-4 mb-6">
                <p className="text-ink/80 mb-2">
                  <strong>Email:</strong> <a href="mailto:legal@dinksocial.com" className="text-primary hover:underline">legal@dinksocial.com</a>
                </p>
                <p className="text-ink/80 mb-2">
                  <strong>Business Address:</strong> 1235 East Blvd, Ste E PMB 2310, Charlotte, NC 28203
                </p>
                <p className="text-ink/80">
                  <strong>Legal Entity:</strong> DINK Social LLC
                </p>
              </div>

              {/* Section 17 */}
              <h2>17. Child Safety Policy</h2>

              <h3>17.1 Zero Tolerance for Child Sexual Abuse and Exploitation</h3>
              <p className="text-ink/75 mb-4">
                DINK Social maintains a zero-tolerance policy regarding Child Sexual Abuse and Exploitation (CSAE). We strictly prohibit any content, activity, or behavior that exploits, endangers, or sexualizes minors in any way. This includes, but is not limited to:
              </p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Child Sexual Abuse Material (CSAM)</li>
                <li>Content that sexualizes minors</li>
                <li>Grooming or predatory behavior toward children</li>
                <li>Trafficking or exploitation of minors</li>
                <li>Any content that facilitates the abuse or exploitation of children</li>
              </ul>

              <h3>17.2 Enforcement and Removal</h3>
              <p className="text-ink/75 mb-3">
                Upon obtaining actual knowledge of any CSAE content or CSAM on our platform, DINK Social will take immediate action, including:
              </p>
              <ul className="list-disc pl-6 text-ink/75 mb-4 space-y-1">
                <li>Immediate removal of the offending content</li>
                <li>Suspension or permanent termination of the associated account</li>
                <li>Preservation of evidence as required by law</li>
              </ul>

              <h3>17.3 Reporting to Authorities</h3>
              <p className="text-ink/75 mb-4">
                DINK Social complies with all applicable child safety laws and regulations. We will report confirmed CSAM and CSAE content to the National Center for Missing & Exploited Children (NCMEC) and cooperate with law enforcement authorities as required by law.
              </p>

              <h3>17.4 Child Safety Contact</h3>
              <p className="text-ink/75 mb-4">
                For child safety concerns or to report potential CSAE content, please contact us at: <a href="mailto:reporting@dinksocial.com" className="text-primary hover:underline">reporting@dinksocial.com</a>
              </p>
              <p className="text-ink/75 mb-6">
                This contact serves as our designated point of contact for receiving notifications regarding CSAE content found on our platform.
              </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
